import type { CoreStoreType } from '../../store/CoreStore';

import type { BaseCoreGameCommunicationProcessor } from './processors/BaseCoreGameCommunicationProcessor';

export interface GameCommunicationErrorMessageType extends GameCommunicationMessageType {
  optExtErrorCode?: string;
  optExtErrorMsg?: string;
}

export interface GameCommunicationMessageType extends Record<string, unknown> {
  beforeInit: boolean;
  delay: number;
  seq: number;
}

export interface ExtractedMessageType {
  amount: number;
  betCode: number;
  bettype: string;
  nc: number;
}
export type GameCommunicationSenderType =
  | ((data: string | ArrayBufferLike | Blob | ArrayBufferView) => void)
  | undefined;

export interface GameCommunicationMetaType {
  connectionStatus: ConnectionStatus;
  connectionProcedure: () => void;
}

/** @deprecated Please Use BaseCoreCommunicationProcessor */
export interface IGameCommunicationProcessor {
  messageName: string;
  delay?: number;
  /* A number value that represents the delay in milliseconds before the processor runs. */
  delayCorrection?: number;
  /* A number value that used to clear message from process queue after process or timeout.  */
  seq?: number;

  /* A number value that represents the correction to be applied to the delay before the processor runs. */
  process(
    message: GameCommunicationMessageType,
    store?: CoreStoreType,
    sender?: GameCommunicationSenderType,
    meta?: GameCommunicationMetaType
  ): void;
}

export type IGameCommunicationProcessorFactory = new (
  store: CoreStoreType
) => BaseCoreGameCommunicationProcessor | IGameCommunicationProcessor;

export interface IGameCommunicationSender<Payload> {
  name: string;
  dispatch(payload: Payload): void;
  send(payload: Payload, store: CoreStoreType, sender: GameCommunicationSenderType): void;
}

export interface IGameCommunicationConnectionObserver {
  update(connectedStatus: ConnectionStatus, store: CoreStoreType): void;
}

export interface GameCommunicationControllerConfig {
  processors?: IGameCommunicationProcessorFactory[];
  senders?: IGameCommunicationSender<unknown>[];
  connectionObserver?: IGameCommunicationConnectionObserver;
}

export interface GameCommunicationControllerConnectorProps {
  gameStore: CoreStoreType;
  config?: GameCommunicationControllerConfig;
}

export interface GameCommunicationTimeout {
  timeout: number;
  messageName: string;
  process: () => void;
  seq: number;
  queuedGameId: string | null;
}

export type AcceptanceState = 'unknown' | 'accepted' | 'not_accepted' | 'rejected';

export enum ConnectionStatus {
  DISCONNECTED, // When connection is not yet established or was stopped (stopStart method in GameCommunicationController)
  CONNECTING, // When connection is being established
  CONNECTED, // When connection is established
  RECONNECTING, // When connection is lost and trying to reconnect
  RECONNECTED, // When connection is re-established after being lost
  CONNECTION_LOST, // When failed to reconnect after a certain number of attempts
  NEW_ROUND, // When a new round is started
}

export const MessageName = {
  Bet: 'bet',
  BetValidationError: 'betValidationError',
  BetsClosed: 'betsclosed',
  BetsClosingSoon: 'betsclosingsoon',
  BetsOpen: 'betsopen',
  Bets: 'bets',
  Canceled: 'canceled',
  Command: 'command',
  Dealer: 'dealer',
  DuplicatedConnection: 'duplicated_connection',
  EnableSubmit: 'enableSubmit',
  GameInit: 'gameInit',
  Game: 'game',
  Message: 'message',
  PrizedropWin: 'prizedrop_win',
  RegulationError: 'regulationError',
  StartDealing: 'startDealing',
  Subscribe: 'subscribe',
  Switch: 'switch',
  TableCloseNotify: 'tableCloseNotify',
  TableClosed: 'table_closed',
  Table: 'table',
  ThirdPartyError: 'thirdpartyerror',
  Timer: 'timer',
  Win: 'win',
  Winners: 'winners',
  Session: 'session',
  Pong: 'pong',
  DuplicatePrivatePlayerSession: 'duplicatPrivatePlayerSession', // new private tables duplicate player msg
  Kickedout: 'kickedout',
  IdleAlert: 'idleAlert',
  PlayerError: 'player_error',
  PlayerBanned: 'PlayerBanned',
  PrivateBCGameStatus: 'privateBCGameStatus',
  TableOccupied: 'table_occupied',
  FreeChipsAvailable: 'freeChipsAvailable',
  TerminateSession: 'terminateSession',
};

/** Special cases. Other than that, show generic error popup */
export enum BetValidationErrors {
  BetCodeError = 1028,
  BetCodeInvalid = 1011,
  BetInvalid = 10002, // ck, gamemode
  BetNotAllowed = 20602,
  BetNotExist = 10005,
  BetNotOnTime = 1007,
  BetRefunded = 20620,
  DidntMeetMaximumTableLimit = 1012,
  DidntMeetMinimumTableLimit = 1013,
  DidntMeetMinimumBetLimit = 1049,
  MaximumBetLimitExceeded = 1048,
  FreeChipUnknownError = 5000,
  FreeChipCampaignError = 5001,
  MainBetNotAvailable = 1041,
  ExternalIgnoreError = 20530,
  ExternalRetryError = 20621,
  GameIdMismatch = 1014,
  GameNotFound = 20038,
  InstantBetsDisabled = 1055,
  InsufficientBalance = 20601,
  InternalServerError = 20003,
  InvalidHashCode = 20999, // operator specific
  InvalidToken = 20510,
  NoResponse = 1001,
  OperatorInternalError = 101020,
  PlayerLimitReached = 20610,
  PlayerNotFound = 20511,
  RealityCheck = 20520,
  RequestError = 1046,
  RequestNoResponse = 1047,
  RequestTimeout = 1050,
  PlayerBonusBetWithoutMainBet = 1059,
  BankerBonusBetWithoutMainBet = 1060,
  UnknownError = 0,
}
